export interface PingRecord {
  client: string;
  task_id: number;
  time: string;
  value: number;
}

export interface PingTaskInfo {
  clients?: string[];
  id: number;
  name: string;
  interval: number;
  loss: number;
  value?: number;
  p99?: number;
  p50?: number;
  p99_p50_ratio?: number;
  min?: number;
  max?: number;
  avg?: number;
  latest?: number;
  total?: number;
  type?: string;
  target?: string;
}

export interface PingRecordsResponse {
  count: number;
  records: PingRecord[];
  tasks: PingTaskInfo[];
  from?: string;
  to?: string;
}

type RPC2Call = <TParams = any, TResult = any>(
  method: string,
  params?: TParams,
  options?: any
) => Promise<TResult>;

type PingRecordsCacheEntry = {
  expiresAt: number;
  data?: PingRecordsResponse;
  promise?: Promise<PingRecordsResponse>;
};

const PING_RECORDS_CACHE_TTL_MS = 60_000;
export const PING_RECORDS_AUTO_REFRESH_MS = 60_000;
const MAX_CONCURRENT_PING_RECORD_REQUESTS = 2;
export const HOME_PING_TASK_DISPLAY_NAME = "家里针实测";
const pingRecordsCache = new Map<string, PingRecordsCacheEntry>();
let pingTasksCache: { expiresAt: number; promise?: Promise<PingTaskInfo[]>; data?: PingTaskInfo[] } | null = null;
let activePingRecordRequests = 0;
const pingRecordRequestQueue: Array<() => void> = [];

const emptyPingRecordsResponse: PingRecordsResponse = {
  count: 0,
  records: [],
  tasks: [],
};

function normalizeUuid(uuid: string | null | undefined): string {
  return typeof uuid === "string" ? uuid.trim() : "";
}

function normalizeHours(hours: number): number {
  return Number.isFinite(hours) && hours > 0 ? hours : 0;
}

function getCacheKey(uuid: string, hours: number): string {
  return `${uuid}:${hours}`;
}

function getTaskCacheKey(taskIds: number[], hours: number): string {
  return `tasks:${taskIds.join(",")}:${hours}`;
}

function parseRecordTimestamp(value: string | null | undefined): number {
  if (!value) return Number.NaN;
  const trimmed = value.trim();
  const match = trimmed.match(
    /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2}):(\d{2})(?:\.\d+)?(Z|[+-]\d{2}:?\d{2})?$/
  );
  if (match) {
    const timezone = match[7] || "Z";
    const iso = `${match[1]}-${match[2]}-${match[3]}T${match[4]}:${match[5]}:${match[6]}${timezone}`;
    const timestamp = Date.parse(iso);
    if (Number.isFinite(timestamp)) return timestamp;
  }
  return Date.parse(trimmed);
}

function toIsoTime(value: string): string {
  const timestamp = parseRecordTimestamp(value);
  return Number.isFinite(timestamp) ? new Date(timestamp).toISOString() : value;
}

function normalizePingRecords(records: PingRecord[]): PingRecord[] {
  return records
    .map((record) => ({
      ...record,
      time: toIsoTime(record.time),
    }))
    .sort((a, b) => parseRecordTimestamp(a.time) - parseRecordTimestamp(b.time));
}

function normalizeResponse(result: Partial<PingRecordsResponse> | null | undefined): PingRecordsResponse {
  const records = normalizePingRecords([...(result?.records || [])]);

  return {
    count: result?.count ?? records.length,
    records,
    tasks: result?.tasks || [],
    from: result?.from,
    to: result?.to,
  };
}

function normalizeTask(item: any): PingTaskInfo | null {
  const id = Number(item?.id);
  if (!Number.isFinite(id)) return null;
  return {
    id,
    name: typeof item?.name === "string" ? item.name : "",
    interval: Number.isFinite(Number(item?.interval)) ? Number(item.interval) : 5,
    loss: Number.isFinite(Number(item?.loss)) ? Number(item.loss) : Number.NaN,
    value: Number.isFinite(Number(item?.value)) ? Number(item.value) : undefined,
    p99: Number.isFinite(Number(item?.p99)) ? Number(item.p99) : undefined,
    p50: Number.isFinite(Number(item?.p50)) ? Number(item.p50) : undefined,
    p99_p50_ratio: Number.isFinite(Number(item?.p99_p50_ratio)) ? Number(item.p99_p50_ratio) : undefined,
    min: Number.isFinite(Number(item?.min)) ? Number(item.min) : undefined,
    max: Number.isFinite(Number(item?.max)) ? Number(item.max) : undefined,
    avg: Number.isFinite(Number(item?.avg)) ? Number(item.avg) : undefined,
    latest: Number.isFinite(Number(item?.latest)) ? Number(item.latest) : undefined,
    total: Number.isFinite(Number(item?.total)) ? Number(item.total) : undefined,
    type: typeof item?.type === "string" ? item.type : undefined,
    target: typeof item?.target === "string" ? item.target : undefined,
    clients: Array.isArray(item?.clients)
      ? item.clients.filter((client: unknown): client is string => typeof client === "string")
      : undefined,
  };
}

function normalizeTaskListResponse(result: any): PingTaskInfo[] {
  const source = Array.isArray(result?.data)
    ? result.data
    : Array.isArray(result)
      ? result
      : [];
  return source
    .map(normalizeTask)
    .filter((task: PingTaskInfo | null): task is PingTaskInfo => task !== null);
}

function extractIpv4(value: string | null | undefined): string {
  const match = (value || "").match(/(?:\d{1,3}\.){3}\d{1,3}/);
  return match ? match[0] : "";
}

function normalizeIpv4(value: string | null | undefined): string {
  return extractIpv4(value);
}

function looksLikeHomePingTask(task: PingTaskInfo): boolean {
  const name = (task.name || "").trim();
  return name === HOME_PING_TASK_DISPLAY_NAME
    || name.startsWith(`${HOME_PING_TASK_DISPLAY_NAME} `)
    || name.includes("[VPS:");
}

function isHomePingTaskForNode(task: PingTaskInfo, uuid: string, ipv4: string): boolean {
  const name = (task.name || "").trim();
  if (!name) return false;

  if (uuid && name.includes(`[VPS:${uuid}]`)) {
    return true;
  }

  const looksLikeHomeTask = looksLikeHomePingTask(task);
  const hasSingleExternalClient = (task.clients?.length || 0) === 1
    && (task.clients ? !task.clients.includes(uuid) : true);
  if (!looksLikeHomeTask && !hasSingleExternalClient) {
    return false;
  }

  if (!ipv4) return false;

  const target = normalizeIpv4(task.target);
  const nameIp = extractIpv4(name);
  return target === ipv4 || nameIp === ipv4 || name.includes(ipv4);
}

function homePingTaskTarget(task: PingTaskInfo, fallbackIpv4: string): string {
  return normalizeIpv4(task.target) || extractIpv4(task.name) || fallbackIpv4;
}

function preferHomePingTask(left: PingTaskInfo, right: PingTaskInfo): PingTaskInfo {
  const leftManaged = left.name.includes("[VPS:");
  const rightManaged = right.name.includes("[VPS:");
  if (leftManaged !== rightManaged) return leftManaged ? left : right;
  return left.id >= right.id ? left : right;
}

function groupHomePingTasks(tasks: PingTaskInfo[], fallbackIpv4: string): Array<{
  primary: PingTaskInfo;
  taskIds: number[];
}> {
  const groups = new Map<string, { primary: PingTaskInfo; taskIds: number[] }>();

  for (const task of tasks) {
    const key = homePingTaskTarget(task, fallbackIpv4);
    const group = groups.get(key);
    if (!group) {
      groups.set(key, { primary: task, taskIds: [task.id] });
      continue;
    }
    group.primary = preferHomePingTask(group.primary, task);
    if (!group.taskIds.includes(task.id)) group.taskIds.push(task.id);
  }

  return [...groups.values()].map((group) => ({
    primary: group.primary,
    taskIds: group.taskIds.sort((a, b) => a - b),
  }));
}

async function fetchPingTasks(options: { forceRefresh?: boolean } = {}): Promise<PingTaskInfo[]> {
  const now = Date.now();
  if (pingTasksCache) {
    if (pingTasksCache.promise) return pingTasksCache.promise;
    if (!options.forceRefresh && pingTasksCache.expiresAt > now && pingTasksCache.data) {
      return pingTasksCache.data;
    }
  }

  const promise = fetch("/api/task/ping")
    .then((response) => {
      if (!response.ok) throw new Error("Failed to fetch ping tasks");
      return response.json();
    })
    .then((result) => {
      const data = normalizeTaskListResponse(result);
      pingTasksCache = {
        data,
        expiresAt: Date.now() + PING_RECORDS_CACHE_TTL_MS,
      };
      return data;
    })
    .catch((error) => {
      pingTasksCache = null;
      throw error;
    });

  pingTasksCache = {
    promise,
    expiresAt: now + PING_RECORDS_CACHE_TTL_MS,
  };

  return promise;
}

function runWithPingRecordRequestLimit<T>(task: () => Promise<T>): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const run = () => {
      activePingRecordRequests++;
      task()
        .then(resolve, reject)
        .finally(() => {
          activePingRecordRequests = Math.max(0, activePingRecordRequests - 1);
          pingRecordRequestQueue.shift()?.();
        });
    };

    if (activePingRecordRequests < MAX_CONCURRENT_PING_RECORD_REQUESTS) {
      run();
      return;
    }

    pingRecordRequestQueue.push(run);
  });
}

export async function fetchPingRecords(
  call: RPC2Call,
  uuid: string | null | undefined,
  hours: number,
  options: { forceRefresh?: boolean } = {}
): Promise<PingRecordsResponse> {
  const normalizedUuid = normalizeUuid(uuid);
  const normalizedHours = normalizeHours(hours);

  if (!normalizedUuid || !normalizedHours) {
    return emptyPingRecordsResponse;
  }

  const now = Date.now();
  const cacheKey = getCacheKey(normalizedUuid, normalizedHours);
  const cached = pingRecordsCache.get(cacheKey);

  if (cached) {
    if (cached.promise) return cached.promise;
    if (!options.forceRefresh && cached.expiresAt > now && cached.data) {
      return cached.data;
    }
  }

  const promise = runWithPingRecordRequestLimit(() =>
    call<any, PingRecordsResponse>("common:getRecords", {
      uuid: normalizedUuid,
      type: "ping",
      hours: normalizedHours,
    })
  )
    .then((result) => {
      const data = normalizeResponse(result);
      pingRecordsCache.set(cacheKey, {
        data,
        expiresAt: Date.now() + PING_RECORDS_CACHE_TTL_MS,
      });
      return data;
    })
    .catch((error) => {
      pingRecordsCache.delete(cacheKey);
      throw error;
    });

  pingRecordsCache.set(cacheKey, {
    promise,
    expiresAt: now + PING_RECORDS_CACHE_TTL_MS,
  });

  return promise;
}

export async function fetchHomePingRecords(
  call: RPC2Call,
  uuid: string | null | undefined,
  ipv4: string | null | undefined,
  hours: number,
  options: { forceRefresh?: boolean } = {}
): Promise<PingRecordsResponse> {
  try {
    return await fetchHomePingRecordsUnsafe(call, uuid, ipv4, hours, options);
  } catch {
    return emptyPingRecordsResponse;
  }
}

async function fetchHomePingRecordsUnsafe(
  call: RPC2Call,
  uuid: string | null | undefined,
  ipv4: string | null | undefined,
  hours: number,
  options: { forceRefresh?: boolean } = {}
): Promise<PingRecordsResponse> {
  const normalizedUuid = normalizeUuid(uuid);
  const normalizedIpv4 = normalizeIpv4(ipv4);
  const normalizedHours = normalizeHours(hours);

  if (!normalizedUuid || !normalizedHours) {
    return emptyPingRecordsResponse;
  }

  const matchingTasks = (await fetchPingTasks(options))
    .filter((task) => (task.type || "icmp").toLowerCase() === "icmp")
    .filter((task) => isHomePingTaskForNode(task, normalizedUuid, normalizedIpv4));
  const taskGroups = groupHomePingTasks(matchingTasks, normalizedIpv4);
  const tasks = taskGroups.map((group) => ({ ...group.primary, name: HOME_PING_TASK_DISPLAY_NAME }));

  if (tasks.length === 0) {
    return emptyPingRecordsResponse;
  }

  const taskIds = taskGroups.flatMap((group) => group.taskIds).sort((a, b) => a - b);
  const canonicalTaskIds = new Map<number, number>();
  taskGroups.forEach((group) => {
    group.taskIds.forEach((taskId) => canonicalTaskIds.set(taskId, group.primary.id));
  });
  const now = Date.now();
  const cacheKey = getTaskCacheKey(taskIds, normalizedHours);
  const cached = pingRecordsCache.get(cacheKey);

  if (cached) {
    if (cached.promise) return cached.promise;
    if (!options.forceRefresh && cached.expiresAt > now && cached.data) {
      return cached.data;
    }
  }

  const end = new Date();
  const start = new Date(end.getTime() - normalizedHours * 60 * 60 * 1000);
  const maxCount = Math.min(4000, Math.max(200, normalizedHours * 60 * 12));

  const promise = runWithPingRecordRequestLimit(async () => {
    const recordLists = await Promise.all(taskIds.map((taskId) =>
      call<any, { records?: PingRecord[] }>("common:getRecords", {
        type: "ping",
        task_id: taskId,
        hours: normalizedHours,
        maxCount,
      }).then((result) => (result?.records || []).map((record) => ({
        ...record,
        time: toIsoTime(record.time),
        task_id: canonicalTaskIds.get(record.task_id) || canonicalTaskIds.get(taskId) || record.task_id,
      })))
    ));
    const records = normalizePingRecords(recordLists.flat());
    return {
      count: records.length,
      records,
      tasks,
      from: start.toISOString(),
      to: end.toISOString(),
    };
  })
    .then((data) => {
      pingRecordsCache.set(cacheKey, {
        data,
        expiresAt: Date.now() + PING_RECORDS_CACHE_TTL_MS,
      });
      return data;
    })
    .catch((error) => {
      pingRecordsCache.delete(cacheKey);
      throw error;
    });

  pingRecordsCache.set(cacheKey, {
    promise,
    expiresAt: now + PING_RECORDS_CACHE_TTL_MS,
  });

  return promise;
}
